import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, BarChart3, Users, Video, ChevronRight } from 'lucide-react';
import { api, formatMoney, downloadCsv } from '../../lib/api';
import { isLiveLike } from '../../lib/course-types';

export default function AdminReports() {
  const [revenue, setRevenue] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/api/admin/reports/revenue'),
      api('/api/admin/reports/completions'),
      api('/api/admin/courses'),
    ])
      .then(([r, c, co]) => {
        setRevenue(r.revenue_by_month || []);
        setCompletions(c.completions || []);
        // The Reports page reviews finished classes, so the picker shows only
        // completed courses: archived ones, or live sessions whose date has
        // already passed. Upcoming/not-yet-run classes are excluded.
        const now = Date.now();
        const isCompleted = (x) => {
          if (x.status === 'archived') return true;
          if (isLiveLike(x.course_type) && x.live_date_utc) {
            const end = new Date(x.live_date_utc).getTime() + (x.duration_minutes || 60) * 60000;
            return end < now;
          }
          return false;
        };
        setCourses((co.courses || []).filter(isCompleted));
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  const exportRevenue = () => {
    downloadCsv(
      revenue.map((r) => ({
        month: new Date(r.month).toISOString().slice(0, 7),
        purchase_count: r.purchase_count,
        revenue_usd: (r.revenue_cents / 100).toFixed(2),
      })),
      `learnable-revenue-${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  const exportCompletions = () => {
    downloadCsv(
      completions.map((c) => ({
        course_title: c.title,
        enrolled: c.enrolled,
        completed: c.completed,
        completion_rate_percent: c.completion_rate ?? 0,
      })),
      `learnable-completions-${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  // Find max revenue value to scale the bars relative to each other
  const maxRev = Math.max(1, ...revenue.map((r) => parseInt(r.revenue_cents, 10) || 0));

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold">Reports</h1>

      {/* Course reports picker */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <BarChart3 size={16} className="text-slate-500" />
          <h2 className="font-semibold">Completed Course Reports</h2>
        </div>
        {courses.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No completed courses yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {courses.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/admin/courses/${c.id}/reports`}
                  state={{ from: 'reports' }}
                  className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isLiveLike(c.course_type) ? (
                      <Video size={16} className="text-brand shrink-0" />
                    ) : (
                      <Users size={16} className="text-slate-400 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{c.title}</div>
                      <div className="text-xs text-slate-500">
                        {c.course_type === 'hybrid' ? 'Hybrid' : c.course_type === 'live' ? 'Live' : c.course_type === 'tutorial' ? 'Tutorial' : 'Self-paced'}
                        {' · '}{c.status}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-slate-400 group-hover:text-slate-600 shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Revenue by month */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold">Revenue by month</h2>
          <button
            onClick={exportRevenue}
            disabled={!revenue.length}
            className="inline-flex items-center gap-2 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-md font-medium disabled:opacity-50"
          >
            <Download size={12} /> CSV
          </button>
        </div>
        {revenue.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No completed purchases yet.</div>
        ) : (
          <div className="p-5 space-y-2">
            {revenue.map((r) => {
              const cents = parseInt(r.revenue_cents, 10) || 0;
              const pct = (cents / maxRev) * 100;
              return (
                <div key={r.month} className="flex items-center gap-3">
                  <div className="w-24 text-xs text-slate-500 shrink-0">
                    {new Date(r.month).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                  </div>
                  <div className="flex-1 h-7 bg-slate-100 rounded relative overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-brand/80 rounded"
                      style={{ width: `${pct}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-end px-2 text-xs font-semibold text-slate-900">
                      {formatMoney(cents)} <span className="text-slate-500 font-normal ml-1">({r.purchase_count})</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Completion rate by course */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold">Completion rate by course</h2>
          <button
            onClick={exportCompletions}
            disabled={!completions.length}
            className="inline-flex items-center gap-2 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-md font-medium disabled:opacity-50"
          >
            <Download size={12} /> CSV
          </button>
        </div>
        {completions.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No courses tracked yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Course</th>
                <th className="text-right px-3 py-3 font-semibold">Enrolled</th>
                <th className="text-right px-3 py-3 font-semibold">Completed</th>
                <th className="text-right px-5 py-3 font-semibold">Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {completions.map((c) => {
                const rate = parseFloat(c.completion_rate || 0);
                return (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3 font-medium text-slate-900">{c.title}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{c.enrolled}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{c.completed}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-slate-200 rounded overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded" style={{ width: `${Math.min(100, rate)}%` }} />
                        </div>
                        <span className="font-semibold tabular-nums w-12 text-right">{rate.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
